/**
 * Typed client for Government Contracting (reference-only).
 *
 * Routes (beam-backend govRouter.ts, /api, Bearer; reads open to any role,
 * mutations owner/office):
 *   GET  /api/gov/certifications
 *   GET  /api/gov/certifications/expiring
 *   POST /api/gov/certifications                     { certification_type, ... }
 *   GET  /api/gov/federal-registrations
 *   GET  /api/gov/solicitations
 *   GET  /api/gov/solicitations/:id
 *   GET  /api/gov/solicitations/:id/match            (non-binding SUGGESTION)
 *   POST /api/gov/solicitations                      { source, title, ... }
 *   POST /api/gov/solicitations/:id/bid-decision     { decision } (records a HUMAN bid/no-bid)
 *   GET  /api/gov/proposals
 *   GET  /api/gov/contracts
 *   POST /api/gov/contracts                          { ... }
 *   GET  /api/gov/compliance-requirements
 *   POST /api/gov/compliance-requirements            { gov_contract_id, requirement, ... }
 *
 * LEGAL / SAFETY: this module is reference-only. The solicitation "match" is a
 * non-binding suggestion — it never bids. `recordBidDecision` only records the
 * human's bid/no-bid choice; BEAM never submits a bid.
 *
 * Endpoints return raw Prisma rows (snake_case); the Row shapes below mirror the
 * actual DB columns (master Appendix A.12). Bearer token via client. NEVER imports
 * @smartbox/*.
 */
import { get, post, patch } from './client';

// ---------------------------------------------------------------------------
// Row shapes (raw snake_case columns from the backend)
// ---------------------------------------------------------------------------

export interface CertificationRow {
  id: string;
  certification_type: string;
  certifying_body: string | null;
  status: string;
  certificate_number: string | null;
  issued_date: string | null;
  expiration_date: string | null;
  naics_codes: string[];
  notes: string | null;
}

export interface FederalRegistrationRow {
  id: string;
  uei: string | null;
  cage_code: string | null;
  registration_status: string;
  registration_date: string | null;
  expiration_date: string | null;
  primary_naics: string | null;
  naics_codes: string[];
  sam_profile_url: string | null;
}

export interface SolicitationRow {
  id: string;
  source: string;
  solicitation_number: string | null;
  title: string;
  issuing_agency: string | null;
  naics_code: string | null;
  set_aside_type: string;
  solicitation_type: string | null;
  status: string;
  posted_date: string | null;
  questions_due: string | null;
  response_due: string | null;
  estimated_value: string | null;
  bid_decision: string | null;
  source_url: string | null;
}

export interface SolicitationMatchRow {
  solicitation_id: string;
  /** 0–100 suggestion score; non-binding. */
  score: number | null;
  recommendation: string | null;
  reasons: string[] | null;
  gaps: string[] | null;
}

export interface ProposalRow {
  id: string;
  solicitation_id: string | null;
  version: number;
  status: string;
  submitted_at: string | null;
  created_at: string;
}

export interface ContractRow {
  id: string;
  solicitation_id: string | null;
  contract_number: string | null;
  awarding_agency: string | null;
  status: string;
  award_amount: string | null;
  currency: string;
  period_start: string | null;
  period_end: string | null;
}

export interface ComplianceRequirementRow {
  id: string;
  gov_contract_id: string;
  requirement: string;
  status: string | null;
  due_date: string | null;
}

// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------

export type CertificationType =
  | 'wosb'
  | 'edwosb'
  | 'sba_small_business'
  | 'c8a'
  | 'hubzone'
  | 'vosb'
  | 'state_dbe'
  | 'other';

export type CertificationStatus = 'prospective' | 'in_progress' | 'certified' | 'expired' | 'denied';

export interface CreateCertificationRequest {
  certification_type: CertificationType;
  certifying_body?: string;
  status?: CertificationStatus;
  certificate_number?: string;
  issued_date?: string;
  expiration_date?: string;
  notes?: string;
}

export type SolicitationSource = 'sam_gov' | 'state_portal' | 'local' | 'prime_subcontract';
export type SetAsideType = 'wosb' | 'edwosb' | 'small_business' | 'c8a' | 'none';
export type SolicitationType = 'rfp' | 'rfq' | 'ifb';

export interface CreateSolicitationRequest {
  source: SolicitationSource;
  title: string;
  solicitation_number?: string;
  issuing_agency?: string;
  naics_code?: string;
  set_aside_type?: SetAsideType;
  solicitation_type?: SolicitationType;
  posted_date?: string;
  questions_due?: string;
  response_due?: string;
  estimated_value?: number;
  source_url?: string;
}

export interface CreateContractRequest {
  solicitation_id?: string;
  contract_number?: string;
  awarding_agency?: string;
  award_amount?: number;
  currency?: string;
  period_start?: string;
  period_end?: string;
}

export interface CreateComplianceRequirementRequest {
  gov_contract_id: string;
  requirement: string;
  due_date?: string;
  status?: string;
}

// ---------------------------------------------------------------------------
// Certifications & registrations
// ---------------------------------------------------------------------------

/** List certifications. */
export async function listCertifications(): Promise<CertificationRow[]> {
  return get<CertificationRow[]>('/api/gov/certifications');
}

/** List certifications expiring soon. */
export async function listExpiringCertifications(): Promise<CertificationRow[]> {
  return get<CertificationRow[]>('/api/gov/certifications/expiring');
}

/** Create / track a certification. */
export async function createCertification(
  body: CreateCertificationRequest,
): Promise<CertificationRow> {
  return post<CertificationRow>('/api/gov/certifications', body);
}

/** List federal registrations (SAM, UEI, etc.). */
export async function listRegistrations(): Promise<FederalRegistrationRow[]> {
  return get<FederalRegistrationRow[]>('/api/gov/federal-registrations');
}

export type FederalRegistrationStatus = 'active' | 'expired' | 'pending';

export interface CreateFederalRegistrationRequest {
  uei?: string;
  cage_code?: string;
  registration_status?: FederalRegistrationStatus;
  registration_date?: string;
  expiration_date?: string;
  primary_naics?: string;
  sam_profile_url?: string;
}

/** Update body: optional string fields may be cleared to null (persisted as an explicit clear). */
export interface UpdateFederalRegistrationRequest {
  uei?: string | null;
  cage_code?: string | null;
  registration_status?: FederalRegistrationStatus;
  registration_date?: string | null;
  expiration_date?: string | null;
  primary_naics?: string | null;
  sam_profile_url?: string | null;
}

/** Create a federal registration (owner/office). Mirrors POST /api/gov/federal-registrations. */
export async function createFederalRegistration(
  body: CreateFederalRegistrationRequest,
): Promise<FederalRegistrationRow> {
  return post<FederalRegistrationRow>('/api/gov/federal-registrations', body);
}

/** Update an existing federal registration (owner/office). Mirrors PATCH /api/gov/federal-registrations/:id. */
export async function updateFederalRegistration(
  id: string,
  body: UpdateFederalRegistrationRequest,
): Promise<FederalRegistrationRow> {
  return patch<FederalRegistrationRow>(`/api/gov/federal-registrations/${id}`, body);
}

// ---------------------------------------------------------------------------
// Solicitations
// ---------------------------------------------------------------------------

/** List solicitations (opportunities). */
export async function listSolicitations(): Promise<SolicitationRow[]> {
  return get<SolicitationRow[]>('/api/gov/solicitations');
}

/** Get a single solicitation. */
export async function getSolicitation(id: string): Promise<SolicitationRow> {
  return get<SolicitationRow>(`/api/gov/solicitations/${id}`);
}

/** Add a solicitation to the tracking queue (owner/office). */
export async function createSolicitation(body: CreateSolicitationRequest): Promise<SolicitationRow> {
  return post<SolicitationRow>('/api/gov/solicitations', body);
}

/**
 * Fetch the NON-BINDING match suggestion for a solicitation. This is a
 * suggestion only — it does not bid or commit to anything.
 */
export async function matchSolicitation(id: string): Promise<SolicitationMatchRow> {
  return get<SolicitationMatchRow>(`/api/gov/solicitations/${id}/match`);
}

/**
 * RECORD a human's bid/no-bid decision. BEAM never submits a bid — this only
 * tracks the choice a person made.
 */
export async function recordBidDecision(id: string, decision: string): Promise<SolicitationRow> {
  return post<SolicitationRow>(`/api/gov/solicitations/${id}/bid-decision`, { decision });
}

// ---------------------------------------------------------------------------
// Proposals / contracts / compliance
// ---------------------------------------------------------------------------

/** List proposals. */
export async function listProposals(): Promise<ProposalRow[]> {
  return get<ProposalRow[]>('/api/gov/proposals');
}

export type ProposalStatus = 'draft' | 'internal_review' | 'submitted' | 'won' | 'lost';

export interface CreateProposalRequest {
  /** The solicitation this proposal responds to (required by the backend). */
  solicitation_id: string;
  /** A government-context estimate (estimate.context === 'government'), optional. */
  estimate_id?: string;
  version?: number;
  status?: ProposalStatus;
  outcome_notes?: string;
}

/** Draft a proposal against a solicitation (owner/office). Mirrors POST /api/gov/proposals. */
export async function createProposal(body: CreateProposalRequest): Promise<ProposalRow> {
  return post<ProposalRow>('/api/gov/proposals', body);
}

/** Advance a proposal's status (owner/office). Mirrors PATCH /api/gov/proposals/:id/status. */
export async function updateProposalStatus(id: string, status: ProposalStatus): Promise<ProposalRow> {
  return patch<ProposalRow>(`/api/gov/proposals/${id}/status`, { status });
}

/** List awarded contracts. */
export async function listContracts(): Promise<ContractRow[]> {
  return get<ContractRow[]>('/api/gov/contracts');
}

/** Track an awarded government contract (owner/office). */
export async function createContract(body: CreateContractRequest): Promise<ContractRow> {
  return post<ContractRow>('/api/gov/contracts', body);
}

/** List compliance requirements. */
export async function listComplianceRequirements(): Promise<ComplianceRequirementRow[]> {
  return get<ComplianceRequirementRow[]>('/api/gov/compliance-requirements');
}

/** Track a compliance requirement under a contract (owner/office). */
export async function createComplianceRequirement(
  body: CreateComplianceRequirementRequest,
): Promise<ComplianceRequirementRow> {
  return post<ComplianceRequirementRow>('/api/gov/compliance-requirements', body);
}
